import React, { useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useNavigate } from 'react-router-dom';
import { getNotifications } from '../../../service/api';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const EmergencyNotifications = () => {
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        priority: '',
        channel: '',
        status: ''
    });

    const priorityColors = {
        Critical: '#dc2626',
        High: '#ea580c',
        Medium: '#f59e0b',
        Low: '#10b981'
    };

    const columnDefs = [
        {
            headerName: 'Notification ID',
            field: 'notificationId',
            sortable: true,
            filter: true,
            width: 150,
            cellRenderer: (params: any) => (
                <span
                    style={{ color: '#2563eb', cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() =>
                        navigate(
                            `/external-emergency-preparedness/notifications/${params.data?._id || params.value}`,
                        )
                    }
                >
                    {params.value}
                </span>
            )
        },
        {
            headerName: 'Title',
            field: 'title',
            sortable: true,
            filter: true,
            flex: 1
        },
        {
            headerName: 'Priority',
            field: 'priority',
            sortable: true,
            filter: true,
            width: 120,
            cellRenderer: (params: any) => (
                <span
                    style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        backgroundColor: (priorityColors as any)[params.value] + '20',
                        color: (priorityColors as any)[params.value],
                        fontWeight: 'bold',
                        fontSize: '12px'
                    }}
                >
                    {params.value}
                </span>
            )
        },
        {
            headerName: 'Channels',
            field: 'channels',
            sortable: false,
            width: 150,
            cellRenderer: (params: any) => (
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {params.value.map((channel: any, idx: number) => (
                        <span
                            key={idx}
                            style={{
                                padding: '2px 8px',
                                borderRadius: '8px',
                                backgroundColor: '#e5e7eb',
                                fontSize: '11px'
                            }}
                        >
                            {channel}
                        </span>
                    ))}
                </div>
            )
        },
        {
            headerName: 'Target',
            field: 'targetType',
            sortable: true,
            filter: true,
            width: 120
        },
        {
            headerName: 'Sent Date',
            field: 'sentAt',
            sortable: true,
            filter: 'agDateColumnFilter',
            width: 180,
            valueFormatter: (params: any) => new Date(params.value).toLocaleString()
        },
        {
            headerName: 'Delivery Rate',
            field: 'deliveryRate',
            sortable: true,
            width: 140,
            cellRenderer: (params: any) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                        width: '60px',
                        height: '8px',
                        backgroundColor: '#e5e7eb',
                        borderRadius: '4px',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            width: `${params.value}%`,
                            height: '100%',
                            backgroundColor: params.value >= 80 ? '#10b981' : params.value >= 50 ? '#f59e0b' : '#dc2626',
                            transition: 'width 0.3s'
                        }} />
                    </div>
                    <span style={{ fontSize: '12px' }}>{params.value}%</span>
                </div>
            )
        },
        {
            headerName: 'Confirmation Rate',
            field: 'confirmationRate',
            sortable: true,
            width: 160,
            cellRenderer: (params: any) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                        width: '60px',
                        height: '8px',
                        backgroundColor: '#e5e7eb',
                        borderRadius: '4px',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            width: `${params.value}%`,
                            height: '100%',
                            backgroundColor: params.value >= 80 ? '#10b981' : params.value >= 50 ? '#f59e0b' : '#dc2626',
                            transition: 'width 0.3s'
                        }} />
                    </div>
                    <span style={{ fontSize: '12px' }}>{params.value}%</span>
                </div>
            )
        }
    ];

    useEffect(() => {
        fetchNotifications();
    }, [filters]);

    const fetchNotifications = async () => {
        try {
            setLoading(true);
            const response = await getNotifications(filters);
            if (response.data.success) {
                setNotifications(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="px-2 py-4 mt-14 min-h-screen">
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px',
                flexWrap: 'wrap',
                gap: '16px'
            }}>
                <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>Emergency Notifications</h1>
                <button
                    onClick={() => navigate('/external-emergency-preparedness/send-notification')}
                    style={{
                        padding: '12px 24px',
                        backgroundColor: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '14px',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 2px 4px rgba(220, 38, 38, 0.2)',
                        transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#b91c1c'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
                >
                    🚨 Send Emergency Notification
                </button>
            </div>

            {/* Filters */}
            <div style={{
                display: 'flex',
                gap: '16px',
                marginBottom: '24px',
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px'
            }}>
                <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                        Priority
                    </label>
                    <select
                        value={filters.priority}
                        onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
                        style={{
                            width: '100%',
                            padding: '8px',
                            borderRadius: '6px',
                            border: '1px solid #d1d5db'
                        }}
                    >
                        <option value="">All</option>
                        <option value="Critical">Critical</option>
                        <option value="High">High</option>
                        <option value="Medium">Medium</option>
                        <option value="Low">Low</option>
                    </select>
                </div>

                <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                        Channel
                    </label>
                    <select
                        value={filters.channel}
                        onChange={(e) => setFilters({ ...filters, channel: e.target.value })}
                        style={{
                            width: '100%',
                            padding: '8px',
                            borderRadius: '6px',
                            border: '1px solid #d1d5db'
                        }}
                    >
                        <option value="">All</option>
                        <option value="SMS">SMS</option>
                        <option value="Email">Email</option>
                        <option value="Push">Push</option>
                    </select>
                </div>

                <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                        Status
                    </label>
                    <select
                        value={filters.status}
                        onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                        style={{
                            width: '100%',
                            padding: '8px',
                            borderRadius: '6px',
                            border: '1px solid #d1d5db'
                        }}
                    >
                        <option value="">All</option>
                        <option value="Sent">Sent</option>
                        <option value="Delivered">Delivered</option>
                        <option value="Read">Read</option>
                        <option value="Confirmed">Confirmed</option>
                        <option value="Failed">Failed</option>
                    </select>
                </div>
            </div>

            {/* AG Grid */}
            <div className="ag-theme-alpine" style={{ height: '600px', width: '100%' }}>
                <AgGridReact
                    rowData={notifications}
                    columnDefs={columnDefs}
                    pagination={true}
                    paginationPageSize={20}
                    domLayout="normal"
                    loading={loading}
                />
            </div>
        </div>
    );
};

export default EmergencyNotifications;
